import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteEmailMonitors } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { EmailMonitor } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteEmailMointorDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedMonitors: string[];
  setSelectedMonitors: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeleteEmailMonitorDialog({
  open,
  handleClose,
  userEmail,
  selectedMonitors,
  setSelectedMonitors,
  changeLogRoles,
}: DeleteEmailMointorDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const monitors = queryClient.getQueryData<EmailMonitor[] | undefined>([
    UserKeys.EmailMonitors,
    userEmail,
  ]);

  const selectedMonitor = monitors?.find(
    (monitor) => monitor.listeningUser === selectedMonitors[0]
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteEmailMonitors(userEmail, selectedMonitors, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedMonitors.length > 1
            ? 'Email monitors deleted successfully!'
            : 'Email monitor deleted successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.EmailMonitors, userEmail],
        (oldData: EmailMonitor[]) =>
          oldData.filter(
            (monitor) => !selectedMonitors.includes(monitor.listeningUser)
          )
      );
      setChangeLogMessage('');
      setSelectedMonitors([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedMonitors.length > 1
            ? Messages.Errors.Network.Deleting('these email monitors')
            : Messages.Errors.Network.Deleting('this email monitor'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.auditing.delete_email_monitor,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.auditing,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.RemoveEmailMonitors.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Delete Monitor"
      dialogContent={
        <Box>
          <Typography>
            {selectedMonitors.length && selectedMonitors.length > 1
              ? `Do you wish to delete the selected ${selectedMonitors.length} email monitors?`
              : selectedMonitors.length
              ? `Do you wish to delete the email monitor for ${selectedMonitor?.listeningUser} monitoring ${userEmail}'s email?`
              : ''}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
